// store/v3/cart.ts — the guest cart (SPEC §3 CartStoreApi, §5, §6)
// Mirrors `store-legacy/cartItems.ts` semantics byte-for-byte (the POS receives
// these objects unchanged) and adds V3 persistence: every mutation is written to
// `services/v3/cache/cartStorage` (debounced 100 ms) and broadcast to the other
// tabs via `services/v3/cache/tabSync`.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { CartItem, CartOptionSelection, PersistedCart } from '~/types/v3/cart'
import type { OptionGroup, Product } from '~/types/v3/entities'
import type { CartStoreApi } from '~/types/v3/stores'
import { buildOptions2, cartKey as buildCartKey } from '~/utils/v3/cartKey'
import { cartTotal } from '~/utils/v3/money'
import { shortId } from '~/utils/v3/ids'
import { loadCart, saveCart } from '~/services/v3/cache/cartStorage'
import { broadcastCart } from '~/services/v3/cache/tabSync'
import { useCatalogStore } from '~/store/v3/catalog'

export type AddItemInput = Product & {
  qty: number
  _options?: OptionGroup[] | null
  comments?: string | null
}

export const useCartStore = defineStore('v3-cart', () => {
  const items = ref<CartItem[]>([])
  const pendingJobId = ref<string | null>(null)
  const key = ref<string | null>(null)

  let pendingPayload: PersistedCart | null = null
  /** true while `restore()` fills the store, so we do not write back what we just read */
  let restoring = false

  const count = computed<number>(() =>
    items.value.reduce((sum, item) => sum + (Number(item?.qty) || 0), 0),
  )

  const total = computed<number>(() => cartTotal(items.value))

  /** Items the kitchen can actually receive: soldout products are dropped. */
  const sendableItems = computed<CartItem[]>(() => {
    const catalog = useCatalogStore()
    return items.value.filter((item) => !catalog.isSoldout(item.productId))
  })

  function snapshot(): PersistedCart | null {
    if (!key.value) return null
    return {
      key: key.value,
      // strip Vue proxies so IndexedDB can structured-clone the payload
      items: JSON.parse(JSON.stringify(items.value)) as CartItem[],
      pendingJobId: pendingJobId.value,
      updatedAt: Date.now(),
      v: 1,
    }
  }

  /**
   * Write-through persist + cross-tab ping. Called by every mutation.
   * The payload is snapshotted synchronously so the write always lands on the
   * key it was made for, even when the store has since switched QR codes.
   * NOT debounced on purpose: the guest can scan the next QR code (or close the
   * tab) a few milliseconds after the last tap, and a deferred write would then
   * never run — the cart would silently be lost.
   */
  function persist(): void {
    if (restoring || !key.value) return
    const payload = snapshot()
    if (!payload) return
    pendingPayload = payload
    void flushPersist()
  }

  /** Writes the pending snapshot now (never drops it). */
  async function flushPersist(): Promise<void> {
    const payload = pendingPayload
    pendingPayload = null
    if (!payload) return
    try {
      await saveCart(payload)
      broadcastCart(payload.key)
    } catch (err: unknown) {
      console.warn('[v3/cart] persist failed', err)
    }
  }

  /** Loads the persisted cart for `key` (empty when there is none) and adopts the key. */
  async function restore(nextKey: string): Promise<void> {
    // Never lose the previous cart (QR switch): flush before adopting a new key.
    await flushPersist()
    restoring = true
    key.value = nextKey || null
    try {
      const persisted = nextKey ? await loadCart(nextKey) : null
      items.value = persisted?.items ?? []
      pendingJobId.value = persisted?.pendingJobId ?? null
    } catch (err) {
      console.warn('[v3/cart] restore failed', err)
      items.value = []
      pendingJobId.value = null
    } finally {
      restoring = false
    }
  }

  /**
   * Legacy `addItem`: an identical line (same `__cartkey`) only bumps qty by ONE,
   * exactly like the legacy store — do not change this to `+= item.qty`.
   */
  function addItem(input: AddItemInput): void {
    const options2 = buildOptions2(input?._options ?? null)
    const __cartkey = buildCartKey(
      { productId: input.productId, price: input.price as number, comments: input.comments ?? null },
      options2,
    )

    const index = items.value.findIndex((i) => i.__cartkey === __cartkey)
    if (index >= 0) {
      items.value[index].qty += 1
      persist()
      return
    }

    items.value.push({
      uuid: shortId(),
      productId: input.productId,
      name: input.name || '',
      name2: (input.name2 as string) || '',
      description: (input.description as string) || '',
      options: input.options || null,
      _options: input._options || null,
      options2: (options2 as CartOptionSelection[]) || null,
      qty: input.qty,
      price: parseFloat(String(input.price)),
      tax: [2, 7],
      discountItem: [0, 0],
      comments: input.comments || null,
      __cartkey,
    })
    persist()
  }

  /** Legacy `updateItem`: replace by uuid, then recompute options2 + __cartkey. */
  function updateItem(modified: CartItem): void {
    const options2 = buildOptions2(modified?._options ?? null)
    const __cartkey = buildCartKey(
      { productId: modified.productId, price: modified.price, comments: modified.comments ?? null },
      options2,
    )
    modified.options2 = options2
    modified.__cartkey = __cartkey

    const index = items.value.findIndex((i) => i.uuid === modified.uuid)
    if (index >= 0) items.value[index] = modified
    persist()
  }

  function removeItem(uuid: string): void {
    const index = items.value.findIndex((i) => i.uuid === uuid)
    if (index >= 0) items.value.splice(index, 1)
    persist()
  }

  /** Empties the cart AND drops the pending job id (a new order gets a new id). */
  function clear(): void {
    items.value.splice(0, items.value.length)
    pendingJobId.value = null
    persist()
  }

  /**
   * Drops every line whose product the POS has marked soldout — the guest can no
   * longer order it, so it should not sit in the cart, count towards the toolbar
   * total or show an `x{qty}` badge on the menu. Returns how many lines went.
   */
  function pruneSoldout(): number {
    const catalog = useCatalogStore()
    const kept = items.value.filter((item) => !catalog.isSoldout(item.productId))
    const dropped = items.value.length - kept.length
    if (dropped === 0) return 0
    items.value = kept
    persist()
    return dropped
  }

  function setPendingJobId(id: string | null): void {
    pendingJobId.value = id
    persist()
  }

  /**
   * Legacy `getQtyByProductId` returns the qty of the FIRST line of that product
   * (not the sum) — the menu-card `x{{qty}}` badge has always shown that number.
   * Kept identical on purpose.
   */
  function qtyByProductId(id: string): number {
    const item = items.value.find((i) => i.productId === id)
    return item ? Number(item.qty) || 0 : 0
  }

  function linesByProductId(id: string): CartItem[] {
    return items.value.filter((item) => item.productId === id)
  }

  return {
    items,
    pendingJobId,
    key,
    count,
    total,
    sendableItems,
    restore,
    addItem,
    updateItem,
    removeItem,
    clear,
    pruneSoldout,
    setPendingJobId,
    qtyByProductId,
    linesByProductId,
  } satisfies CartStoreApi
})

export type CartStore = ReturnType<typeof useCartStore>
